// invoiceItemsModel.js
//
// Field reads, pricing math, and rollups for Invoice Items.

import { parsePriceCents } from 'utilities/Currency';
import { buildNodeFieldPatch, readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { getNodelistPrimaryKey } from 'utilities/NodelistField';
import {
  INVOICE_ITEM_TYPE_KEY,
  readInvoiceField,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';
import {
  computePurchaseOrderItemLineTotal,
  toMoneyNumber,
  toQuantityNumber,
} from 'nodetypes/purchaseorder/view/purchaseOrderItemsModel';
import { sumPaidDocumentPayments } from 'nodetypes/documents/payments/documentPaymentsModel';

function normalizeString(value) {
  return String(value || '').trim();
}

function normalizeTypeKey(value) {
  return normalizeString(value).toUpperCase();
}

/**
 * @param {object|null|undefined} item
 * @param {string} fieldName
 * @returns {unknown}
 */
export function readInvoiceItemField(item, fieldName) {
  return readNodeFieldRawValue(
    item,
    { name: fieldName, source: 'invoice_item' },
    INVOICE_ITEM_TYPE_KEY,
  );
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toPercentNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
}

/**
 * Markup amount in cents: unitPrice − cost.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarkupAmountCents(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null) return null;
  return priceCents - costCents;
}

/**
 * Unit price from cost + markup percent.
 *
 * @param {unknown} cost
 * @param {unknown} markupPercent
 * @returns {number|null}
 */
export function computeUnitPriceFromCostAndMarkup(cost, markupPercent) {
  const costCents = toMoneyNumber(cost);
  const markup = toPercentNumber(markupPercent);
  if (costCents == null) return null;
  if (markup == null) return costCents;
  return Math.round(costCents * (1 + (markup / 100)));
}

/**
 * Markup % from cost + unit price.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarkupPercentFromCostAndPrice(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null || costCents === 0) return null;
  return Math.round(((priceCents - costCents) / costCents) * 10000) / 100;
}

/**
 * Margin % from cost + unit price: (price − cost) / price.
 *
 * @param {unknown} cost
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeMarginPercent(cost, unitPrice) {
  const costCents = toMoneyNumber(cost);
  const priceCents = toMoneyNumber(unitPrice);
  if (costCents == null || priceCents == null || priceCents === 0) return null;
  return Math.round(((priceCents - costCents) / priceCents) * 10000) / 100;
}

/**
 * Line total: qty × unitPrice (sell).
 *
 * @param {unknown} quantity
 * @param {unknown} unitPrice
 * @returns {number|null}
 */
export function computeInvoiceItemLineTotal(quantity, unitPrice) {
  return computePurchaseOrderItemLineTotal(quantity, unitPrice);
}

/**
 * @param {object|null|undefined} item
 * @returns {number|null}
 */
export function resolveInvoiceItemLineTotal(item) {
  const quantity = toQuantityNumber(readInvoiceItemField(item, 'quantity'));
  const unitPrice = toMoneyNumber(readInvoiceItemField(item, 'unitPrice'));
  const derived = computeInvoiceItemLineTotal(quantity, unitPrice);
  if (derived != null) return derived;
  return toMoneyNumber(readInvoiceItemField(item, 'total'));
}

/**
 * Merge computed pricing fields into a field patch when cost / markup / unitPrice / qty change.
 *
 * @param {object} options
 * @returns {object|null|undefined}
 */
export function augmentInvoiceItemPricingPatch({
  node,
  nodeTypeKey,
  field,
  value,
  patch,
}) {
  if (!patch || !node) return patch;
  if (normalizeTypeKey(nodeTypeKey || node?.type) !== INVOICE_ITEM_TYPE_KEY) {
    return patch;
  }

  const fieldName = normalizeString(field?.name);
  const pricingFields = new Set(['quantity', 'cost', 'markupPercent', 'unitPrice']);
  if (!pricingFields.has(fieldName)) return patch;

  let cost = fieldName === 'cost' ? value : readInvoiceItemField(node, 'cost');
  let markupPercent = fieldName === 'markupPercent'
    ? value
    : readInvoiceItemField(node, 'markupPercent');
  let unitPrice = fieldName === 'unitPrice'
    ? value
    : readInvoiceItemField(node, 'unitPrice');
  const quantity = fieldName === 'quantity'
    ? value
    : readInvoiceItemField(node, 'quantity');

  if (fieldName === 'cost') {
    const nextPrice = computeUnitPriceFromCostAndMarkup(cost, markupPercent);
    if (nextPrice != null) unitPrice = nextPrice;
    else if (toMoneyNumber(unitPrice) != null) {
      markupPercent = computeMarkupPercentFromCostAndPrice(cost, unitPrice);
    }
  } else if (fieldName === 'markupPercent') {
    const nextPrice = computeUnitPriceFromCostAndMarkup(cost, markupPercent);
    if (nextPrice != null) unitPrice = nextPrice;
  } else if (fieldName === 'unitPrice') {
    if (toMoneyNumber(cost) != null) {
      markupPercent = computeMarkupPercentFromCostAndPrice(cost, unitPrice);
    }
  }

  const marginPercent = computeMarginPercent(cost, unitPrice);
  const total = computeInvoiceItemLineTotal(quantity, unitPrice);

  const source = field?.source || 'invoice_item';
  const merged = { ...patch };

  const applyExtra = (name, type, nextValue) => {
    if (nextValue === undefined) return;
    const extraField = { name, source, type };
    const extraPatch = buildNodeFieldPatch(node, extraField, nodeTypeKey, nextValue);
    if (!extraPatch) return;
    Object.entries(extraPatch).forEach(([dataKey]) => {
      const existingSection = (
        merged[dataKey] && typeof merged[dataKey] === 'object'
          ? merged[dataKey]
          : {}
      );
      merged[dataKey] = {
        ...existingSection,
        [name]: nextValue,
      };
    });
  };

  if (fieldName !== 'cost' && toMoneyNumber(cost) != null) {
    // cost unchanged — no write
  }
  if (fieldName !== 'unitPrice' && unitPrice !== readInvoiceItemField(node, 'unitPrice')) {
    applyExtra('unitPrice', 'money', unitPrice);
  }
  if (fieldName !== 'markupPercent' && markupPercent !== readInvoiceItemField(node, 'markupPercent')) {
    applyExtra('markupPercent', 'percentage', markupPercent);
  }
  if (marginPercent != null) {
    applyExtra('marginPercent', 'percentage', marginPercent);
  }
  if (total != null) {
    applyExtra('total', 'money', total);
  }

  return merged;
}

/**
 * @param {object|null|undefined} item
 * @param {object|null|undefined} [itemType]
 * @param {string} [language='en']
 * @returns {object}
 */
export function getInvoiceItemRowModel(item, itemType = null, language = 'en') {
  const fields = Array.isArray(itemType?.schema?.fields) ? itemType.schema.fields : [];
  const enumLabel = (fieldName, value) => {
    const cleaned = normalizeString(value);
    if (!cleaned) return '';
    const field = fields.find((row) => normalizeString(row?.name) === fieldName);
    if (!field) return cleaned;
    const options = resolveEnumOptionsForField(field, language);
    return getEnumOptionLabel(options, cleaned, language) || cleaned;
  };

  const cost = toMoneyNumber(readInvoiceItemField(item, 'cost'));
  const unitPrice = toMoneyNumber(readInvoiceItemField(item, 'unitPrice'));
  const markupPercent = toPercentNumber(readInvoiceItemField(item, 'markupPercent'));
  const storedMargin = toPercentNumber(readInvoiceItemField(item, 'marginPercent'));
  const marginPercent = storedMargin != null
    ? storedMargin
    : computeMarginPercent(cost, unitPrice);
  const markupAmount = computeMarkupAmountCents(cost, unitPrice);

  const unitValue = normalizeString(readInvoiceItemField(item, 'unit'));
  const description = normalizeString(getNodeSubtitle(item));
  const leadTime = normalizeString(readInvoiceItemField(item, 'leadTime'));

  return {
    key: normalizeString(item?.key),
    title: normalizeString(item?.title),
    description,
    subtitle: description || leadTime,
    leadTime,
    coverUrl: normalizeString(item?.coverUrl),
    sku: normalizeString(readInvoiceItemField(item, 'sku')),
    manufacturer: normalizeString(readInvoiceItemField(item, 'manufacturer')),
    vendorKey: getNodelistPrimaryKey(readInvoiceItemField(item, 'vendor')) || '',
    roomKey: getNodelistPrimaryKey(readInvoiceItemField(item, 'room')) || '',
    itemType: normalizeString(readInvoiceItemField(item, 'itemType')),
    itemTypeLabel: enumLabel('itemType', readInvoiceItemField(item, 'itemType')),
    category: normalizeString(readInvoiceItemField(item, 'category')),
    categoryLabel: enumLabel('category', readInvoiceItemField(item, 'category')),
    status: normalizeString(readInvoiceItemField(item, 'status')),
    quantity: toQuantityNumber(readInvoiceItemField(item, 'quantity')),
    unit: unitValue,
    unitLabel: enumLabel('unit', unitValue) || unitValue,
    cost,
    msrp: toMoneyNumber(readInvoiceItemField(item, 'msrp')),
    markupPercent,
    markupAmount,
    marginPercent,
    unitPrice,
    color: normalizeString(readInvoiceItemField(item, 'color')),
    dimensions: normalizeString(readInvoiceItemField(item, 'dimensions')),
    shipping: toMoneyNumber(readInvoiceItemField(item, 'shipping')),
    shippingMarkupPercent: toPercentNumber(readInvoiceItemField(item, 'shippingMarkupPercent')),
    taxable: readInvoiceItemField(item, 'taxable') !== false,
    website: normalizeString(readInvoiceItemField(item, 'website')),
    total: resolveInvoiceItemLineTotal(item),
  };
}

/**
 * @param {object[]} items
 * @param {object|null|undefined} invoiceNode
 * @param {object|null|undefined} [invoiceType]
 * @param {object[]|null} [payments]
 * @returns {{ subTotal: number, tax: number, total: number, amountPaid: number, balanceDue: number }}
 */
export function computeInvoiceItemsRollups(
  items,
  invoiceNode = null,
  invoiceType = null,
  payments = null,
) {
  const subTotal = (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const lineTotal = resolveInvoiceItemLineTotal(item);
    return sum + (lineTotal == null ? 0 : lineTotal);
  }, 0);

  const tax = toMoneyNumber(readInvoiceField(invoiceNode, 'tax', invoiceType)) || 0;
  const total = subTotal + tax;

  let amountPaid;
  if (Array.isArray(payments) && payments.length > 0) {
    amountPaid = sumPaidDocumentPayments(payments);
  } else {
    amountPaid = toMoneyNumber(readInvoiceField(invoiceNode, 'amountPaid', invoiceType)) || 0;
  }

  const balanceDue = total - amountPaid;

  return {
    subTotal,
    tax,
    total,
    amountPaid,
    balanceDue,
  };
}
